import { Component, OnInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, Input, HostListener, ElementRef, DestroyRef, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LatLng } from 'src/app/models/latlng';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { EvCalculationService } from 'src/app/services/core/ev-calculation.service';
import { MobileResolutionService } from 'src/app/services/platform/mobile-resolution.service';
import { MapService } from 'src/app/services/map/map.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { ActivatedRoute } from '@angular/router';
import { NavigationService } from 'src/app/services/core/navigation.service';
import { MatDialog } from '@angular/material/dialog';
import { GpsErrorDialogComponent } from '../../modals/gps-error-dialog/gps-error-dialog.component';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { AccountService } from 'src/app/services/account/account.service';
import { SettingsDialogComponent } from '../../modals/settings-dialog/settings-dialog.component';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { AccountDialogComponent } from '../../modals/account-dialog/account-dialog.component';
import { fromEvent, concatMap, takeUntil } from 'rxjs';
import { MapElementType } from 'src/app/models/map-element-type';
import { Route, RoutePlanElement, RoutePlanType, Turn } from 'src/app/models/route';
import { SearchedRouteElement } from '../../../models/searched-route-element';
import { RouteListElement } from 'src/app/models/route-list';
import { Position } from 'src/app/models/position';
import { LocationService } from 'src/app/services/platform/location.service';
import { NavigationMenuComponent } from '../../navigation/navigation-menu/navigation-menu.component';
import { CommonModule } from '@angular/common';
import { PlanningWaypointsComponent } from './planning-waypoints/planning-waypoints.component';
import { TrackInformationComponent } from './track-information/track-information.component';
import { RoutePlanComponent } from './route-plan/route-plan.component';
import { ItineraryComponent } from './itinerary/itinerary.component';
import { EvLoaderComponent } from '../../ui-components/ev-loader/ev-loader.component';
import { DisplayMode } from 'src/app/models/display-mode';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { ModalRef } from 'src/app/services/modal';
import { MapDrawerHelperService } from 'src/app/services/map/map-drawer-helper.service';
const PLANNING_PANEL_MIN_HEIGHT = 255;

@Component({
  standalone: true,
  selector: 'app-planning-panel',
  templateUrl: './planning-panel.component.html',
  styleUrls: ['./planning-panel.component.scss'],
  imports: [NavigationMenuComponent, CommonModule,
    PlanningWaypointsComponent, TrackInformationComponent,
    RoutePlanComponent, ItineraryComponent, EvLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningPanelComponent implements OnInit {
  @Input() loadingMap?: string | null;
  @ViewChild('paneDragOuter', { static: true }) paneDragOuter!: ElementRef<HTMLElement>;
  @HostListener('window:resize') onResize() {
    this.viewportSize.set({ width: window.innerWidth, height: window.innerHeight });
    this.planningPanelHeight.set(this.getPlanningPanelDefaultHeight());

    if (!this.navigation() && (window.innerWidth <= 1050 || window.innerHeight <= 700) && this.orientationPortrait != this.mobileResolutionService.isPortrait()) {
      this.orientationPortrait = this.mobileResolutionService.isPortrait();
      this.planningPanelHeight.set(PLANNING_PANEL_MIN_HEIGHT);
      this.planningPanelSetMobileHeight.set(this.planningPanelHeight());
    }
  }

  public readonly isAuthorized = signal<boolean>(false);
  public readonly mobileVisiblePanel = signal<string>("");
  public readonly mobileVisibleRoutePanel = signal<string>("routepoints");
  public readonly mobileResolution = signal<boolean | undefined>(undefined);
  private readonly mapDrawerHelperService = inject(MapDrawerHelperService);
  /**
   * On the compact layout the panel and a map card (POI card, charging popup)
   * share a small screen and easily overlap or exceed it, so the panel steps
   * aside while a card is open and returns when the card closes.
   */
  public readonly hiddenForMapCard = computed(() =>
    !!this.mobileResolution() && this.mapDrawerHelperService.mapCardOpen());
  public readonly routeList = signal<RouteListElement[]>([]);
  public routeData!: Route;
  public readonly selectedDisplayMode = signal<DisplayMode>(DisplayMode.RANGE);
  public readonly DisplayMode = DisplayMode;
  public readonly navigation = signal<boolean>(false);
  public readonly itineraryNextTurnIdx = signal<number>(-1);
  public readonly failedPlan = signal<boolean>(false);
  public readonly maxReachedWaypoint = signal<number>(0);
  public readonly noRoute = signal<boolean>(false);
  private readonly destroyRef = inject(DestroyRef);
  public readonly demo = signal<boolean>(false);
  public readonly roadLanesLength = signal<number>(0);
  public readonly emptySearchInput = signal<boolean>(false);
  public readonly lastHudComputers = signal<number>(1);
  private orientationPortrait: boolean = false;

  public readonly planningPanelSetMobileHeight = signal<number>(PLANNING_PANEL_MIN_HEIGHT);
  public readonly planningPanelHeight = signal<number>(this.getPlanningPanelDefaultHeight());
  /** Refreshed on resize so the expanded height below follows rotation and the soft keyboard. */
  private readonly viewportSize = signal<{ width: number; height: number }>({ width: window.innerWidth, height: window.innerHeight });
  /** Index of the waypoint row whose search dropdown is open, or null when none is. */
  private readonly openSearchRow = signal<number | null>(null);
  /**
   * Height bound to the panel on the compact layout. The panel is short there
   * and clips its content, so a search dropdown on one of the lower waypoint
   * rows would be mostly hidden. While a dropdown is open the panel grows to
   * the same height the drag handle expands it to; the user's own height is
   * restored as soon as the dropdown closes.
   */
  public readonly effectivePanelHeight = computed(() => {
    const height = this.planningPanelHeight();
    if (this.openSearchRow() === null) {
      return height;
    }
    const { width, height: viewportHeight } = this.viewportSize();
    return Math.max(height, this.getPlanningPanelMaxHeight(width, viewportHeight));
  });
  public readonly activePlanningPanelRouteElement = signal<number | null>(-1);

  /* display planned route default data, itinerary, charging plan */
  constructor(private waypointService: WaypointService, private evCalculationService: EvCalculationService, public mobileResolutionService: MobileResolutionService, private mapService: MapService,
    private cdr: ChangeDetectorRef, public utilsService: UtilsService, private navigationService: NavigationService,
    public webviewService: WebviewService, public MatDialog: MatDialog, public settingsService: SettingsService,
    public languageService: LanguageService, public accountService: AccountService, private consoleLoggerService: ConsoleLoggerService,
    private activatedRoute: ActivatedRoute, private locationService: LocationService, private stackedModalsService: StackedModalsService) {

    this.orientationPortrait = this.mobileResolutionService.isPortrait();

    if (this.activatedRoute?.snapshot?.routeConfig?.path == "demo") {
      this.demo.set(true);
    }
  }

  ngOnChanges(): void {
    if (this.mobileResolution() != undefined) {
      // Still needed: this component renders @Input state (routeData, waypoints) that is not signal-based.
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    this.languageService.activeLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        if (this.selectedDisplayMode() == DisplayMode.ROUTE && this.routeData) {
          this.routeData.Turns = this.mapService.initTurnIconAndInstruction(this.routeData.Turns);
        }
        // Still needed: routeData is a plain @Input object mutated just above, not a signal.
        this.cdr.detectChanges();
      }
    });

    // observe when get route is selected
    this.waypointService.ObservableSelectedDisplayMode.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.selectedDisplayMode.set(resp);
      }
      if (resp == DisplayMode.RANGE) {
        this.failedPlan.set(false);
      }
    });

    // observe route list changes from service (replaces ObservableAddEndPoint, ObservableAddRoutePoint,
    // ObservableSetStartPointGeocode, ObservableWaypointsRouteListParams)
    this.waypointService.ObservableRouteList.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null) {
        this.routeList.set(resp);
        this.checkEmptySearchInput();
      }
    });

    this.waypointService.ObservableNoRoute.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null) {
        this.noRoute.set(resp);
        if (this.noRoute()) this.failedPlan.set(false);
      }
    });

    // observe mobile resolution
    this.mobileResolutionService.ObservableMobileVisiblePanel.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      this.mobileVisiblePanel.set(resp);
    });

    this.mobileResolutionService.ObservableMobileVisibleRoutePanel.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.mobileVisibleRoutePanel.set(resp);
      }
    });

    this.mobileResolutionService.ObservableMobileResolution.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      this.mobileResolution.set(resp);
    });

    // observe route planned
    this.mapService.ObservableLastRouteData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.initNewPlannedRoute(resp);
      }
    });

    this.navigationService.ObservableChangeNavigation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp && resp == "start") {
        this.navigation.set(true);
        this.waypointService.removeEmptyRouteListEntries();
      }
      if (resp && resp == "exit") {
        this.navigation.set(false);
      }
      if (resp && resp == "arrive") {
        this.navigation.set(false);
        this.waypointService.resetToFirstWaypoint();
      }
      this.checkEmptySearchInput();
      this.planningPanelHeight.set(this.getPlanningPanelDefaultHeight());
    });

    // highlight the next turn
    this.navigationService.ObservableNextTurn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp && this.navigation()) {

        this.itineraryNextTurnIdx.set(resp.NextTurnInstruction.Idx);
        this.roadLanesLength.set(resp.NextTurnInstruction.LaneInfo.length);

        const actHudComputers = (resp.DistToNextChargingStation != null || resp.DistToNextWaypoint != null) ? 2 : 1;
        if (this.lastHudComputers() != actHudComputers) {
          this.lastHudComputers.set(actHudComputers);
        }
      }
    });

    // observe failed plan state
    this.evCalculationService.ObservableFailedPlan.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != undefined && resp != null) {
        this.failedPlan.set(resp.failedPlan);
        this.maxReachedWaypoint.set(resp.maxReachedWaypoint);
      }
    });

    this.accountService.ObservableIsAuthorized.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != undefined) {
        this.isAuthorized.set(resp);
      }
    });

    this.mapService.ObservableActivePlanningPanelRouteElement.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null && resp != undefined) {
        if (resp.type === 'start') {
          this.activePlanningPanelRouteElement.set(0);
        }
        else if (resp.type === 'clear') {
          this.activePlanningPanelRouteElement.set(null);
        }
        else if (resp.type === 'end') {
          this.activePlanningPanelRouteElement.set(this.routeData.RoutePlan.length - 1);
        }
        else if (resp.type === 'charger') {
          let routePlanIndex = 0;
          let chargerIndex = 0;
          while (chargerIndex <= resp.index! && routePlanIndex < this.routeData.RoutePlan.length) {
            if (this.routeData.RoutePlan[routePlanIndex].Type === RoutePlanType.Charger) {
              chargerIndex++;
            }
            routePlanIndex++;
          }
          this.activePlanningPanelRouteElement.set(routePlanIndex - 1);
        }
        else if (resp.type === 'waypoint') {
          let routePlanIndex = 0;
          let waypointIndex = 0;
          while (waypointIndex <= resp.index! && routePlanIndex < this.routeData.RoutePlan.length) {
            if (this.routeData.RoutePlan[routePlanIndex].Type === RoutePlanType.WayPoint) {
              waypointIndex++;
            }
            routePlanIndex++;
          }
          this.activePlanningPanelRouteElement.set(routePlanIndex - 1);
        }
      }
    });
  }

  testGpsCount: number = 0;
  testGpsEl: Position = new Position(0, 0, 0, 0, 0, 0, 0, 0);

  // changing searchbox text, after context menu action
  ngAfterViewInit(): void {
    this.waypointService.ObservableReverseGeocodedLocations.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        // Addresses belong to the waypoints that were planned; if the count no longer
        // matches, they are from an abandoned plan and must not rebuild the list.
        if (resp.length !== this.waypointService.getWaypointsParams().length) return;
        const current = this.waypointService.getRouteList();
        const newList: RouteListElement[] = resp.map((addr, i) => {
          const existing = current[i];
          // Charging stations already have their name set — don't overwrite with a geocoded street address
          const display_name = existing?.csid
            ? existing.display_name
            : this.mapService.parseAddressString(addr, addr.Location.lat, addr.Location.lng);
          return { display_name, lat: addr.Location.lat, lng: addr.Location.lng, wp: i, csid: existing?.csid };
        });
        this.waypointService.setRouteListDisplay(newList);
        this.checkEmptySearchInput();
      }
    });

    this.initPaneEvents();
  }

  private initPaneEvents(): void {
    // --------------- Pane click events ---------------
    const pDown = fromEvent<MouseEvent>(this.paneDragOuter.nativeElement, 'mousedown');
    const pMove = fromEvent<MouseEvent>(document, 'mousemove');
    const pUp = fromEvent<MouseEvent>(document, 'mouseup')
    const pDrag = pDown.pipe(concatMap(DragEvent => pMove.pipe(takeUntil(pUp))));
    let lastStartCoord = 0;

    pDrag.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((md: MouseEvent) => {
      this.planningPanelHeight.set(md.pageY - 55);
      this.checkPlanningPanelLimits();
      this.planningPanelSetMobileHeight.set(this.planningPanelHeight());
    });
    pUp.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mu: MouseEvent) => {
      if (lastStartCoord) {
        if (mu.pageY > lastStartCoord) {
          this.planningPanelHeight.set(this.getPlanningPanelMaxHeight(window.innerWidth, window.innerHeight));
        }
        else {
          this.planningPanelHeight.set(PLANNING_PANEL_MIN_HEIGHT);
        }
        lastStartCoord = 0;
      }
    });

    // --------------- Pane touch events ---------------
    const pTouchStart = fromEvent<TouchEvent>(this.paneDragOuter.nativeElement, 'touchstart');
    const pTouchMove = fromEvent<TouchEvent>(this.paneDragOuter.nativeElement, 'touchmove');
    const pTouchEnd = fromEvent<TouchEvent>(this.paneDragOuter.nativeElement, 'touchend');
    const pTouchDrag = pTouchStart.pipe(concatMap(DragEvent => pTouchMove.pipe(takeUntil(pTouchEnd))));

    pTouchDrag.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((td: TouchEvent) => {
      this.planningPanelHeight.set(td.changedTouches[0].pageY - 55);
      this.checkPlanningPanelLimits();
      this.planningPanelSetMobileHeight.set(this.planningPanelHeight());
    });
    pTouchStart.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((td: TouchEvent) => {
      lastStartCoord = td.changedTouches[0].pageY;
    });
    pTouchEnd.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((te: TouchEvent) => {
      if (lastStartCoord) {
        if (te.changedTouches[0].pageY > lastStartCoord) {
          this.planningPanelHeight.set(this.getPlanningPanelMaxHeight(window.innerWidth, window.innerHeight));
        }
        else {
          this.planningPanelHeight.set(PLANNING_PANEL_MIN_HEIGHT);
        }
        lastStartCoord = 0;
      }
    });
  }

  public get RoutePlanType(): typeof RoutePlanType {
    return RoutePlanType;
  }

  public getPlanningPanelDefaultHeight(): number {
    if (window.innerWidth <= 1050 || window.innerHeight <= 700) {
      return this.planningPanelSetMobileHeight();
    }
    else {
      if (this.navigation()) {
        return window.innerHeight - 69;
      }
      else {
        return window.innerHeight - 55;
      }
    }
  }

  /** Tallest the panel gets on the compact layout: most of the screen in landscape, 3/5 of it in portrait. */
  private getPlanningPanelMaxHeight(viewportWidth: number, viewportHeight: number): number {
    return viewportWidth > viewportHeight
      ? viewportHeight / 5 * 4.4
      : viewportHeight / 5 * 3;
  }

  private checkPlanningPanelLimits(): void {
    if (this.planningPanelHeight() < PLANNING_PANEL_MIN_HEIGHT) {
      this.planningPanelHeight.set(PLANNING_PANEL_MIN_HEIGHT);
    }
    const maxHeight = this.getPlanningPanelMaxHeight(window.innerWidth, window.innerHeight);
    if (this.planningPanelHeight() > maxHeight) {
      this.planningPanelHeight.set(maxHeight);
    }
  }

  private initNewPlannedRoute(routeData: Route): void {
    // route basic data
    this.routeData = routeData;
    this.routeData.Turns = this.mapService.initTurnIconAndInstruction(this.routeData.Turns);

    this.noRoute.set(false);
    this.waypointService.ObservableNoRoute.next(false);

    // position map after route loaded on the mobile devices
    setTimeout(() => {
      this.mapService.ObservablePositionMapToRoute.next(this.waypointService.getWaypointsParams().map((el) => {
        return new LatLng(el.lat, el.lng)
      }));
    }, 100);
  }

  // set start coord after search
  public setStartCoordsParamToSearchedResult(): void {
    this.mapService.ObservableSetFollowModeRange.next(false);
    const searchData = this.mapService.getSearchedLocation()!;
    this.waypointService.setWaypointAtIndex(0, {
      display_name: searchData.display_name,
      lat: searchData.lat,
      lng: searchData.lon,
      wp: 0
    });
    this.waypointService.setSearchedRangeOrRoute(true);
  }

  // set end coord after search
  public setEndCoordsParamToSearchedResult(): void {
    const searchData = this.mapService.getSearchedLocation()!;
    this.waypointService.setWaypointAtIndex(1, {
      display_name: searchData.display_name,
      lat: searchData.lat,
      lng: searchData.lon,
      wp: 1
    });
    this.waypointService.setSearchedRangeOrRoute(true);
  }

  public setSearchedToHome(): void {
    if (!this.isAuthorized()) {
      this.openAccount();
    }
    else {
      let homeAddress = this.settingsService.getHomeAddress();
      if (homeAddress?.Description) {
        let homeElement = {
          display_name: homeAddress.Description,
          lat: homeAddress.Location.lat,
          lon: homeAddress.Location.lng,
          boundingbox: homeAddress.BoundingBox
        }
        this.searchChangedHandler(homeElement);
      }
      else {
        this.openSettings();
      }
    }
  }

  public setSearchedToWork(): void {
    if (!this.isAuthorized()) {
      this.openAccount();
    }
    else {
      let workAddress = this.settingsService.getWorkAddress();
      if (workAddress?.Description) {
        let workElement = {
          display_name: workAddress.Description,
          lat: workAddress.Location.lat,
          lon: workAddress.Location.lng,
          boundingbox: workAddress.BoundingBox
        }
        this.searchChangedHandler(workElement);
      }
      else {
        this.openSettings();
      }
    }
  }

  public stopTrackingDialog(): ModalRef<ConfirmationDialogComponent> {
    return this.stackedModalsService.openModal(ConfirmationDialogComponent, {
      data:
      {
        title: this.languageService.languageJSON.Map_StopTracking,
        message: this.languageService.languageJSON.Map_StopTracking_Message,
        buttons: [this.languageService.languageJSON.Global_Stop, this.languageService.languageJSON.Global_Cancel]
      }, autoFocus: false
    });
  }

  // adding searched location to service
  public searchChangedHandler(searchData: SearchedRouteElement): void {
    this.mapService.setSearchedLocation(searchData);
  }

  private checkEmptySearchInput(): void {
    let emptyCount = 0;
    for (let i = 0; i < this.routeList().length; i++) {
      if (this.routeList()[i].lat == 0 && this.routeList()[i].lng == 0) {
        emptyCount++;
      }
    }

    if (emptyCount == 0) {
      this.emptySearchInput.set(false);
    }
    else {
      this.emptySearchInput.set(true);
    }
  }

  public waypointSearchActiveHandler(event: { index: number; active: boolean }): void {
    if (event.active) {
      this.openSearchRow.set(event.index);
    }
    // Moving focus from one row to another reports the new row's open before
    // the old row's close, so a close only counts for the row still recorded.
    else if (this.openSearchRow() === event.index) {
      this.openSearchRow.set(null);
    }
  }

  public navigateToTurn(turn: Turn): void {
    if (!this.navigation()) {
      if (window.innerWidth <= 1050 || window.innerHeight <= 700) {
        this.planningPanelHeight.set(PLANNING_PANEL_MIN_HEIGHT);
      }

      this.mapService.setSelectedCoordinate(this.routeData.RoutePoints[turn.RoutePointIdxStart].Location);
    }
  }

  // navigate to location
  public openRoutePlanPoint(routeEl: RoutePlanElement, index?: number): void {
    if (!this.navigation()) {
      if (window.innerWidth <= 1050 || window.innerHeight <= 700) {
        this.planningPanelHeight.set(PLANNING_PANEL_MIN_HEIGHT);
      }

      this.mapService.setSelectedCoordinate(routeEl.Location);
      if (index != undefined) {
        this.activePlanningPanelRouteElement.set(index);
      }

      if (routeEl.Type == RoutePlanType.Charger) {
        this.mapService.getChargingStationDetail([routeEl.ChargingStationId!]).subscribe((resp) => {
          // The route element already knows where the charger is, so a detail
          // response without coordinates (or none at all) can still be shown.
          const details = resp?.[0];
          this.mapService.setSearchedMapElement(MapElementType.Chargingstation, {
            ...details,
            id: details?.id ?? routeEl.ChargingStationId,
            lat: Number.isFinite(details?.lat) ? details!.lat : routeEl.Location.lat,
            lon: Number.isFinite(details?.lon) ? details!.lon : routeEl.Location.lng,
          });
        });
      }
    }
  }

  // open error dialog modal
  public openGpsErrorDialog(): void {
    if (!this.webviewService.IsGPSTWebview()) {
      this.stackedModalsService.openModalWindow(GpsErrorDialogComponent);
    }
  }

  // change unit
  public openSettings(): void {
    this.stackedModalsService.openModal(SettingsDialogComponent,
      { panelClass: 'dialog-snap-to-fullscreen', disableClose: true, autoFocus: false });
  }

  public openAccount(): void {
      this.stackedModalsService.openModal(AccountDialogComponent, {
        data: { process: 'signIn' },
        panelClass: 'dialog-snap-to-fullscreen'
      });
  }
}
